import { componentDocsURL } from './docs-url.js';
import type { FeatureManifestSummary } from './manifest.js';

/**
 * Shared per-feature header builder. Both `init`'s yml generator and
 * `add-feature`'s AST mutator consume this: the generator emits each
 * line as a `# `-prefixed string in a line-array, the mutator joins
 * the lines into a yaml-lib `commentBefore` string on the new
 * feature pair.
 *
 * Returns the wrapped paragraph lines WITHOUT a `#` prefix or leading
 * space — the consumer adds whichever convention applies (`# Foo` for
 * the generator, ` Foo` for yaml-lib's stored-after-`#` form).
 *
 * Format:
 *   - `<Name>: <description>` (one paragraph, wrapped)
 *   - `<usageNote>` (one paragraph per note, wrapped)
 *   - the feature's page on getmonoceros.build
 *
 * The option table and the vendor's own URL used to sit between the two, which
 * put a paragraph of documentation above every entry, maintained separately
 * from the page that documents the same thing properly. The options are visible
 * in the block below with their values; what they mean belongs on the page.
 *
 * An empty / unknown manifest summary returns `[]` — the caller emits
 * just the `- ref:` line without prose. Same fallback shape as the
 * generator's documented-mode third-party path.
 */
export function buildFeatureHeaderLines(
  summary: FeatureManifestSummary | undefined,
  width: number,
): string[] {
  const paragraphs = buildHeaderParagraphs(summary);
  const wrapped: string[] = [];
  for (const para of paragraphs) {
    for (const line of wrapToComment(para, width)) {
      wrapped.push(line);
    }
  }
  return wrapped;
}

/**
 * Same as `buildFeatureHeaderLines` but each line gets a leading
 * single space, matching yaml-lib's `commentBefore` storage
 * convention (which prepends `#` and prints the body verbatim). Use
 * when setting `commentBefore` on a Scalar or Pair node.
 */
export function buildFeatureHeaderCommentBefore(
  summary: FeatureManifestSummary | undefined,
  width: number,
): string {
  const lines = buildFeatureHeaderLines(summary, width);
  return lines.map((l) => ` ${l}`).join('\n');
}

function buildHeaderParagraphs(
  summary: FeatureManifestSummary | undefined,
): string[] {
  if (!summary) return [];
  const out: string[] = [];
  const tagline = summary.name?.trim();
  const description = summary.description?.trim();
  if (tagline && description) {
    out.push(`${tagline}: ${description}`);
  } else if (tagline) {
    out.push(tagline);
  } else if (description) {
    out.push(description);
  }
  for (const note of summary.usageNotes) {
    const trimmed = note.trim();
    if (trimmed.length > 0) out.push(trimmed);
  }
  // Catalog features only. A third-party ref never gets a summary at all, so
  // there is no case here where a link would point at a page that does not
  // exist.
  if (summary.docsSlug.length > 0) {
    out.push(componentDocsURL('feature', summary.docsSlug));
  }
  return out;
}

/**
 * Word-wrap a single paragraph of plain text to `width` columns. The
 * returned strings do NOT include any prefix — the caller is expected
 * to prepend a comment marker (`# `) and indent. Long words that
 * exceed `width` are emitted on their own line rather than split
 * mid-word.
 */
export function wrapToComment(text: string, width: number): string[] {
  const words = text.split(/\s+/).filter((w) => w.length > 0);
  if (words.length === 0) return [''];
  const usable = Math.max(width, 20);
  const lines: string[] = [];
  let current = '';
  for (const w of words) {
    if (current.length === 0) {
      current = w;
      continue;
    }
    if (current.length + 1 + w.length <= usable) {
      current += ' ' + w;
    } else {
      lines.push(current);
      current = w;
    }
  }
  if (current.length > 0) lines.push(current);
  return lines;
}

/** Default comment width matching the generator's. */
export const FEATURE_HEADER_WIDTH = 76 - 2; // COMMENT_WIDTH - "# " prefix

/**
 * The repo-provider CLI features expose their access token under the
 * PROVIDER name, not the feature id (ADR 0031). The token is the repo
 * credential, resolved by the `GIT_TOKEN__<PROVIDER>_…` convention, so
 * `GITHUB_API_TOKEN` reads better than the id-derived `GITHUB_CLI_API_TOKEN`
 * and lines up with the convention the builder already uses. This is the
 * one deliberate exception to the generic rule below.
 */
const PROVIDER_TOKEN_VAR: Record<string, string> = {
  'github-cli': 'GITHUB_API_TOKEN',
  'gitlab-cli': 'GITLAB_API_TOKEN',
};

/**
 * Derive the `<name>.env` variable name for a feature option, used as
 * the `${VAR}` placeholder in the yml and the seeded key in the env
 * file. Generic rule `<FEATURE_ID>_<OPTION>`, applied uniformly:
 *   atlassian:1   + apiToken      → ATLASSIAN_API_TOKEN
 *   claude-code:1 + apiKey        → CLAUDE_CODE_API_KEY
 *
 * The one exception is the repo-provider CLI features' `apiToken`
 * (github-cli / gitlab-cli → GITHUB_API_TOKEN / GITLAB_API_TOKEN); see
 * `PROVIDER_TOKEN_VAR`.
 *
 * It is a monoceros-side placeholder key — NOT the env var the tool
 * itself reads (the value is passed as the feature's option), so a
 * predictable derived name is honest.
 */
export function featureOptionVarName(ref: string, optionKey: string): string {
  const leaf = ref.split('/').pop() ?? ref;
  const id = leaf.split('@')[0]!.split(':')[0]!;
  if (optionKey === 'apiToken' && PROVIDER_TOKEN_VAR[id]) {
    return PROVIDER_TOKEN_VAR[id]!;
  }
  const idSnake = id.replace(/[^A-Za-z0-9]+/g, '_').toUpperCase();
  const optSnake = optionKey
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .toUpperCase();
  return `${idSnake}_${optSnake}`;
}

export interface FeatureOptionHint {
  /** Option key, e.g. `apiToken`. */
  key: string;
  /** Derived env var name, e.g. `ATLASSIAN_API_TOKEN`. */
  envVar: string;
  /** yml placeholder, e.g. `${ATLASSIAN_API_TOKEN}`. */
  placeholder: string;
}

/**
 * The credential-bearing option hints for a feature (from the manifest's
 * `x-monoceros.optionHints`), minus any keys already set with an active
 * value. Shared by the init generator (renders `${VAR}` hint lines) and
 * `add-feature` (renders the same as a node comment) and the `.env`
 * seeding (uses `envVar`). Empty for unknown/third-party refs (no
 * manifest → no hints).
 */
export function featureOptionHints(
  summary: FeatureManifestSummary | undefined,
  ref: string,
  activeKeys: readonly string[] = [],
): FeatureOptionHint[] {
  return (summary?.optionHints ?? [])
    .filter((key) => !activeKeys.includes(key))
    .map((key) => {
      const envVar = featureOptionVarName(ref, key);
      return { key, envVar, placeholder: `\${${envVar}}` };
    });
}

/**
 * The commented `plugins:` scaffold for a feature whose agent reads plugins.
 * Returned without the leading `#` on each line — the caller (the init
 * generator) or yaml-lib (the `add-feature` AST writer) adds it, the same
 * shape as a service's `exampleVolumesComment`.
 *
 * The whole block stays commented, key included: an active but empty
 * `plugins:` parses to null and apply rejects it. The example is a real,
 * complete entry rather than placeholders in angle brackets, so uncommenting
 * it produces something that parses, and editing it is one word at a time.
 *
 * Returns undefined for a feature that has no example (and therefore no
 * plugins).
 */
export function examplePluginsComment(
  example: { url: string; enable: readonly string[] } | undefined,
): string | undefined {
  if (!example) return undefined;
  return [
    ' plugins:',
    `   - url: ${example.url}`,
    '     enable:',
    ...example.enable.map((name) => `       - ${name}`),
  ].join('\n');
}
